import React from 'react';

import { colors } from '@/const/colors';
import { Button } from '@mui/material';

export interface ButtonProps {
  name: string;
  description?: React.ReactNode;
  descriptionColor?: string;
  icon?: React.ReactNode;
  img?: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  fontSize: number;
  fontWeight: 'normal' | 'medium' | 'semibold' | 'bold';
  onClick: () => void;
  width?: number | string;
  height?: number | string;
  border?: string;
  borderRadius?: number;
  isDisabled?: boolean;
  className?: string;
  shadow?: boolean;
  justify?: 'start' | 'center' | 'end';
}

const isGradient = (value?: string) => !!value && value.includes('gradient(');

function BasicButton({
  name,
  description,
  descriptionColor,
  icon,
  img,
  bgColor,
  border,
  borderColor,
  textColor,
  fontSize,
  fontWeight,
  onClick,
  width,
  height,
  borderRadius,
  isDisabled,
  className,
  shadow = false,
  justify = 'center',
}: ButtonProps) {
  const borderIsGradient = isGradient(borderColor);
  const textIsGradient = isGradient(textColor);

  const gradientBorderBackground = `linear-gradient(${bgColor}, ${bgColor}) padding-box, ${borderColor} border-box`;

  const gradientTextStyle: React.CSSProperties = {
    backgroundImage: textColor,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  };

  return (
    <Button
      className="w-full"
      onClick={() => onClick()}
      sx={{
        fontSize: fontSize,
        lineHeight: `${fontSize + 4}px`,
        color: textIsGradient ? 'transparent' : textColor,
        borderRadius: `${borderRadius ? borderRadius + 'px' : '10px'}`,
        ...(borderIsGradient
          ? {
              background: gradientBorderBackground,
              border: `${border ? border : '1px solid'} transparent`,
              '&:hover': {
                background: gradientBorderBackground,
              },
            }
          : {
              bgcolor: bgColor,
              border: `${border ? border : '1px solid'} ${borderColor}`,
              '&:hover': {
                backgroundColor: bgColor,
              },
            }),
        minWidth: width ? width : '',
        width: width ? width : '100%',
        height: height ? height : '50',
        minHeight: height ? height : '50',

        '&.Mui-disabled': {
          color: bgColor === colors.gray[0] ? colors.gray[4] : colors.gray[6],
          backgroundColor: bgColor === colors.gray[0] ? colors.gray[2] : colors.gray[4],
          border: bgColor === colors.gray[0] ? `1px solid ${colors.gray[3]}` : 'none',
        },
        '&.MuiButtonBase-root': {
          justifyContent: justify,
        },
        boxShadow: shadow ? '0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3)' : '',
      }}
      disableRipple
      disabled={isDisabled}
    >
      <div className={`flex flex-col ${!!description ? 'w-full px-2 py-2' : ''}`}>
        <div className={`flex justify-center items-center font-${fontWeight} ${className}`}>
          {img && <img src={img} alt={name} className="w-5 h-5 mr-2" />}
          {icon && <span className="mr-2">{icon}</span>}
          <span className="break-keep" style={textIsGradient ? gradientTextStyle : undefined}>
            {name}
          </span>
        </div>
        {!!description &&
          (typeof description === 'string' ? (
            <div
              className="text-[13px] leading-[16px] text-center mt-2"
              style={{ color: descriptionColor }}
              dangerouslySetInnerHTML={{ __html: description }}
            ></div>
          ) : (
            <div className="mt-3 normal-case">{description}</div>
          ))}
      </div>
    </Button>
  );
}

export default BasicButton;
